// ./src/hooks/usePollQuestions.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  PollQuestion,
  PollQuestionService,
  PollQuestionFilter,
  PollQuestionInput,
  PollQuestionBulkInput,
  Pagination,
  SortItem,
  listPage,
} from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UsePollQuestionsOptions {
  pageSize?: number;
  initialFilters?: Partial<PollQuestionFilter>;
  initialSort?: SortItem[];
  autoLoad?: boolean;
}

export interface UsePollQuestionsReturn {
  pollQuestion: PollQuestion | null;
  pollQuestions: PollQuestion[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  isCreating: boolean;
  isUpdating: boolean;
  isBulkUpdating: boolean;
  removingIds: Set<string>;
  isRemoving: (id?: string) => boolean;

  filters: Partial<PollQuestionFilter>;
  sortBy: SortItem[];

  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<PollQuestionFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: PollQuestionInput) => Promise<OpResult>;
  update: (id: string, params: PollQuestionInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;
  /** Mass-change status on many pollQuestions. */
  bulkUpdate: (ids: string[], params: PollQuestionBulkInput) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  reset: () => void;
  getById: (id: string | undefined) => PollQuestion | undefined;

  totalCount: number;

  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Defaults
// ============================================

const DEFAULT_SORT: SortItem[] = [
  { name: "orderNo", order: "ASC" },
  { name: "question", order: "ASC" },
];

const DEFAULT_FILTERS: Partial<PollQuestionFilter> = {};

// ============================================
// Hook
// ============================================

export const usePollQuestions = (options: UsePollQuestionsOptions = {}): UsePollQuestionsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  const lastQueriedFilterRef = useRef<Partial<PollQuestionFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  const [pollQuestion, setPollQuestion] = useState<PollQuestion | null>(null);
  const [pollQuestions, setPollQuestions] = useState<PollQuestion[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [filters, setFilters] = useState<Partial<PollQuestionFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const [lastError, setLastError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(() => isLoaded && pollQuestions.length === 0, [isLoaded, pollQuestions.length]);

  // ── Removal tracking ──
  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setPollQuestion(null);
    setPollQuestions([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsBulkUpdating(false);
    setRemovingIds(new Set());
  }, []);

  // ── Core reads ──
  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await PollQuestionService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as PollQuestion[])[0];
        setPollQuestion(data);
        setPollQuestions((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setPollQuestion(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setPollQuestion(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const list = useCallback(
    async (
      filterOverride: Partial<PollQuestionFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const result = await listPage(
          PollQuestionService.list,
          filterOverride as PollQuestionFilter,
          { page: targetPage, pageSize: targetPageSize },
          currentSortBy
        );

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? (result.data as PollQuestion[]) : [];

          if (isAppending) {
            setPollQuestions((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setPollQuestions(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setPollQuestions([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setPollQuestions([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const getMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    if (ids.length === 0) {
      return new OpResult();
    }

    setLoading(true);
    setLastError(null);

    try {
      const result = await PollQuestionService.getMany(ids);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const loadedItems = Array.isArray(result.data) ? (result.data as PollQuestion[]) : [];
        setPollQuestions((prev) => upsertManyBy(prev, loadedItems));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Mutations ──
  const create = useCallback(async (params: PollQuestionInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await PollQuestionService.create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as PollQuestion[])[0];
        setPollQuestion(newItem);
        setPollQuestions((prev) => upsertBy(prev, newItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(async (id: string, params: PollQuestionInput): Promise<OpResult> => {
    if (!id) {
      const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Poll question ID is required for updating");
      setLastError(result);

      return result;
    }

    setIsUpdating(true);
    setLastError(null);

    try {
      const result = await PollQuestionService.update({ id }, params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedItem = (result.data as PollQuestion[])[0];
        setPollQuestion(updatedItem);
        setPollQuestions((prev) => upsertBy(prev, updatedItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Poll question ID is required for removal");
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await PollQuestionService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setPollQuestion(null);
          setPollQuestions((prev) => removeBy(prev, id));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one poll question must be selected");
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await PollQuestionService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setPollQuestion(null);
          setPollQuestions((prev) => removeManyBy(prev, ids));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  const bulkUpdate = useCallback(
    async (ids: string[], params: PollQuestionBulkInput): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one poll question must be selected");
        setLastError(result);

        return result;
      }

      setIsBulkUpdating(true);
      setLastError(null);

      try {
        const result = await PollQuestionService.bulkUpdate(ids, params);

        if (result.code === OP_RESULT_CODES.OK) {
          const updatedItems = Array.isArray(result.data) ? (result.data as PollQuestion[]) : [];
          setPollQuestions((prev) => upsertManyBy(prev, updatedItems));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsBulkUpdating(false);
      }
    },
    []
  );

  // ── Convenience ──
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  const getById = useCallback(
    (id: string | undefined): PollQuestion | undefined => {
      if (!id) {
        return undefined;
      }

      return pollQuestions.find((item) => item.id === id);
    },
    [pollQuestions]
  );

  const totalCount = useMemo(() => pollQuestions.length, [pollQuestions]);

  // ── Effects ──
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ── Return ──
  return useMemo(
    () => ({
      pollQuestion,
      pollQuestions,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    }),
    [
      pollQuestion,
      pollQuestions,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    ]
  );
};
